interface SoundMap {
  [key: string]: HTMLAudioElement;
}

class SoundService {
  private sounds: SoundMap = {};
  private isEnabled: boolean = true;
  private volume: number = 0.7;

  constructor() {
    // Preload sounds
    this.loadSound('playCard', '/sounds/play_cards.wav');
    
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('gameAudioSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      this.isEnabled = settings.enabled ?? true;
      this.volume = settings.volume ?? 0.7;
    }
  }

  private loadSound(name: string, path: string): void {
    try {
      this.sounds[name] = new Audio(path);
      this.sounds[name].volume = this.volume;
      this.sounds[name].preload = 'auto';
    } catch (error) {
      console.warn(`Failed to load sound ${name}:`, error);
    }
  }

  private saveSettings(): void {
    localStorage.setItem('gameAudioSettings', JSON.stringify({
      enabled: this.isEnabled,
      volume: this.volume
    }));
  }

  play(soundName: string): void {
    if (!this.isEnabled || !this.sounds[soundName]) {
      return;
    }

    try {
      // Reset the audio to beginning in case it's already playing
      this.sounds[soundName].currentTime = 0;
      this.sounds[soundName].play().catch(error => {
        console.warn(`Failed to play sound ${soundName}:`, error);
      });
    } catch (error) {
      console.warn(`Error playing sound ${soundName}:`, error);
    }
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    Object.values(this.sounds).forEach(sound => {
      sound.volume = this.volume;
    });
    this.saveSettings();
  }

  toggle(): boolean {
    this.isEnabled = !this.isEnabled;
    this.saveSettings();
    return this.isEnabled;
  }

  enable(): void {
    this.isEnabled = true;
    this.saveSettings();
  }

  disable(): void {
    this.isEnabled = false;
    this.saveSettings();
  }

  isAudioEnabled(): boolean {
    return this.isEnabled;
  }

  getVolume(): number {
    return this.volume;
  }

  playCardSound(): void {
    this.play('playCard');
  }
}

// Create singleton instance
const soundService = new SoundService();

export default soundService;